import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Tags,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ContractTypesPage = () => {
  const [loading, setLoading] = useState(true);
  const [contractTypes, setContractTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  
  // Form states  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: 'bg-blue-100 text-blue-800',
    is_active: true,
    prefix: '',
    category: 'commercial'
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    is_active: 'all'
  });

  const [submitting, setSubmitting] = useState(false);

  // Color options for contract types
  const COLOR_OPTIONS = [
    { value: 'bg-blue-100 text-blue-800', label: 'Azul', preview: 'bg-blue-100 border-blue-200' },
    { value: 'bg-green-100 text-green-800', label: 'Verde', preview: 'bg-green-100 border-green-200' },
    { value: 'bg-purple-100 text-purple-800', label: 'Roxo', preview: 'bg-purple-100 border-purple-200' },
    { value: 'bg-orange-100 text-orange-800', label: 'Laranja', preview: 'bg-orange-100 border-orange-200' },
    { value: 'bg-cyan-100 text-cyan-800', label: 'Ciano', preview: 'bg-cyan-100 border-cyan-200' },
    { value: 'bg-yellow-100 text-yellow-800', label: 'Amarelo', preview: 'bg-yellow-100 border-yellow-200' },
    { value: 'bg-red-100 text-red-800', label: 'Vermelho', preview: 'bg-red-100 border-red-200' },
    { value: 'bg-gray-100 text-gray-800', label: 'Cinza', preview: 'bg-gray-100 border-gray-200' }
  ];

  // Category options
  const CATEGORY_OPTIONS = [
    { value: 'commercial', label: 'Comercial' },
    { value: 'hr', label: 'Recursos Humanos' },
    { value: 'legal', label: 'Jurídico' },
    { value: 'infrastructure', label: 'Infraestrutura' },
    { value: 'other', label: 'Outros' }
  ];

  useEffect(() => {
    loadContractTypes();
  }, []);

  useEffect(() => {
    loadContractTypes();
  }, [filters]);

  const loadContractTypes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.is_active !== 'all') params.append('is_active', filters.is_active === 'true');

      const response = await axios.get(`${API}/api/contract-types?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractTypes(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de contrato:', error);
      toast.error('Erro ao carregar tipos de contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/api/contract-types`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Tipo de contrato criado com sucesso!');
      resetForm();
      setShowCreateModal(false);
      await loadContractTypes();

    } catch (error) {
      console.error('Erro ao criar tipo de contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar tipo de contrato');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditType = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/api/contract-types/${selectedType.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Tipo de contrato atualizado com sucesso!');
      resetForm();
      setShowEditModal(false);
      setSelectedType(null);
      await loadContractTypes();

    } catch (error) {
      console.error('Erro ao atualizar tipo de contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar tipo de contrato');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteType = async (typeId, typeName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o tipo "${typeName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/contract-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Tipo de contrato excluído com sucesso!');
      await loadContractTypes();

    } catch (error) {
      console.error('Erro ao excluir tipo de contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir tipo de contrato');
    }
  };

  const handleToggleActive = async (typeId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/contract-types/${typeId}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Tipo de contrato ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      await loadContractTypes();

    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do tipo de contrato');
    }
  };

  const openEditModal = (type) => {
    setSelectedType(type);
    setFormData({
      name: type.name || '',
      description: type.description || '',
      color: type.color || 'bg-blue-100 text-blue-800',
      is_active: type.is_active !== false,
      prefix: type.prefix || '',
      category: type.category || 'commercial'
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: 'bg-blue-100 text-blue-800',
      is_active: true,
      prefix: '',
      category: 'commercial'
    });
  };

  const getColorPreview = (colorClass) => {
    const colorOption = COLOR_OPTIONS.find(option => option.value === colorClass);
    return colorOption ? colorOption.preview : 'bg-gray-100 border-gray-200';
  };

  const getCategoryLabel = (category) => {
    const categoryOption = CATEGORY_OPTIONS.find(option => option.value === category);
    return categoryOption ? categoryOption.label : category;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Tags className="h-8 w-8 text-blue-600" />
            Tipos de Contratos
          </h1>
          <p className="text-gray-600 mt-2">
            Configure os tipos de contratos disponíveis no sistema
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={loadContractTypes}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Tipo
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Tipos</p>
                <p className="text-2xl font-bold text-blue-600">{contractTypes.length}</p>
              </div>
              <Tags className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tipos Ativos</p>
                <p className="text-2xl font-bold text-green-600">
                  {contractTypes.filter(type => type.is_active).length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tipos Inativos</p>
                <p className="text-2xl font-bold text-red-600">
                  {contractTypes.filter(type => !type.is_active).length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Mais Usado</p>
                <p className="text-lg font-bold text-purple-600">
                  {contractTypes.length > 0 ? contractTypes[0]?.name || 'N/A' : 'N/A'}
                </p>
              </div>
              <FileText className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Tipos de Contratos</CardTitle>
          <CardDescription>
            Gerencie todos os tipos de contratos do sistema
          </CardDescription>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar tipos..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-64"
              />
            </div>
            
            <select
              value={filters.is_active}
              onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="true">Apenas Ativos</option>
              <option value="false">Apenas Inativos</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Carregando tipos de contrato...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Prefixo</TableHead>
                  <TableHead>Cor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractTypes.map(type => (
                  <TableRow key={type.id}>
                    <TableCell>
                      <div className="font-medium">{type.name}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {type.description || 'Sem descrição'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getCategoryLabel(type.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {type.prefix || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border-2 ${getColorPreview(type.color)}`}></div>
                        <Badge className={type.color}>
                          Exemplo
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={type.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {type.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(type)}
                          title="Editar tipo"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(type.id, type.is_active)}
                          className={type.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                          title={type.is_active ? 'Desativar' : 'Ativar'}
                        >
                          {type.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteType(type.id, type.name)}
                          className="text-red-600 hover:text-red-700"
                          title="Excluir tipo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {contractTypes.length === 0 && !loading && (
            <div className="text-center py-12">
              <Tags className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum tipo encontrado</h3>
              <p className="text-gray-500 mb-4">
                Crie seu primeiro tipo de contrato para começar
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Tipo
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar Novo Tipo de Contrato</DialogTitle>
            <DialogDescription>
              Configure um novo tipo de contrato para o sistema
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateType}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome do Tipo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Prestação de Serviços"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="prefix">Prefixo</Label>
                  <Input
                    id="prefix"
                    value={formData.prefix}
                    onChange={(e) => setFormData(prev => ({ ...prev, prefix: e.target.value.toUpperCase() }))}
                    placeholder="Ex: SRV, EMP, SUP"
                    maxLength={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Usado na numeração automática dos contratos
                  </p>
                </div>

                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORY_OPTIONS.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva este tipo de contrato"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="color">Cor da Badge</Label>
                  <select
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {COLOR_OPTIONS.map(color => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <Badge className={formData.color}>
                      Prévia da Cor
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="is_active">Tipo Ativo</Label>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Tipo
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Tipo de Contrato</DialogTitle>
            <DialogDescription>
              Modifique as configurações do tipo de contrato
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditType}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_name">Nome do Tipo *</Label>
                  <Input
                    id="edit_name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Prestação de Serviços"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="edit_prefix">Prefixo</Label>
                  <Input
                    id="edit_prefix"
                    value={formData.prefix}
                    onChange={(e) => setFormData(prev => ({ ...prev, prefix: e.target.value.toUpperCase() }))}
                    placeholder="Ex: SRV, EMP, SUP"
                    maxLength={3}
                  />
                </div>

                <div>
                  <Label htmlFor="edit_category">Categoria</Label>
                  <select
                    id="edit_category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORY_OPTIONS.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit_description">Descrição</Label>
                  <Textarea
                    id="edit_description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva este tipo de contrato"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="edit_color">Cor da Badge</Label>
                  <select
                    id="edit_color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {COLOR_OPTIONS.map(color => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <Badge className={formData.color}>
                      Prévia da Cor
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="edit_is_active">Tipo Ativo</Label>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractTypesPage;